import { Puzzle, Compass } from 'lucide-react';

/**
 * Shared building blocks for SuperAdmin capability pages.
 *
 * Many of these pages are UI scaffolds created so the design team can see the
 * full surface of the SuperAdmin control plane. Each scaffold declares which
 * backend endpoints it is meant to wire to (status="ready") or notes that the
 * backend does not exist yet (status="planned"). Once the redesign is done,
 * wiring is a matter of replacing the placeholder data with the listed calls.
 */

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

/**
 * Banner that documents the backend contract for a page.
 *
 * @param {'ready'|'planned'} status
 *   - 'ready'   : backend endpoint(s) exist; this UI just needs wiring.
 *   - 'planned' : capability is required but no backend exists yet.
 * @param {string[]} endpoints  list of "METHOD /path" strings
 * @param {string}   note       extra free text
 */
export function ScaffoldBanner({ status = 'ready', endpoints = [], note }) {
  const isPlanned = status === 'planned';
  const Icon = isPlanned ? Compass : Puzzle;
  return (
    <div className={`scaffold-banner scaffold-banner--${isPlanned ? 'planned' : 'ready'}`}>
      <div className="scaffold-banner__icon">
        <Icon size={18} color={isPlanned ? 'var(--status-warning)' : 'var(--accent-primary)'} />
      </div>
      <div className="scaffold-banner__body">
        <div className="scaffold-banner__title">
          {isPlanned
            ? 'Planned capability — no backend yet'
            : 'UI scaffold — backend ready, not yet wired'}
        </div>
        <div className="scaffold-banner__text">
          {isPlanned
            ? 'This screen represents a capability the platform needs. There is no API for it yet; it must be built on the backend before it can be wired.'
            : 'The endpoints below already exist in the backend. This screen is a visual placeholder for the redesign — after the new design lands, connect these calls to make it live.'}
          {note ? ` ${note}` : ''}
          {endpoints.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {endpoints.map((e) => (
                <span key={e} className="endpoint-pill">{e}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Section({ title, desc, actions, children }) {
  return (
    <div className="section">
      <div className="section__header">
        <div>
          <div className="section__title">{title}</div>
          {desc && <div className="section__desc">{desc}</div>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function StatTile({ label, value, hint }) {
  return (
    <div className="stat-tile">
      <div className="stat-tile__label">{label}</div>
      <div className="stat-tile__value">{value}</div>
      {hint && <div className="stat-tile__hint">{hint}</div>}
    </div>
  );
}
